/**
 * Typed API methods for chat plugin.
 */
import { request } from '@reflex/ui/client/http';
import type {
  AgentCapabilityAnswer,
  ChatModelStatus,
  Conversation,
  ConversationWithMessages,
  CreateConversationRequest,
  UpdateConversationRequest,
} from '@reflex/shared';
import type {
  TranscribeRequest,
  TranscribeResponse,
  TranscriptionCapability,
} from '../shared/transcription.types.js';

export function getEnabledModels(): Promise<ChatModelStatus[]> {
  return request<ChatModelStatus[]>('/chat/models');
}

export function listConversations(): Promise<Conversation[]> {
  return request<Conversation[]>('/conversations');
}

export function getConversation(id: string): Promise<ConversationWithMessages> {
  return request<ConversationWithMessages>(`/conversations/${encodeURIComponent(id)}`);
}

export function createConversation(
  data?: CreateConversationRequest,
): Promise<ConversationWithMessages> {
  return request<ConversationWithMessages>('/conversations', {
    method: 'POST',
    body: JSON.stringify(data ?? {}),
  });
}

export function updateConversation(
  id: string,
  data: UpdateConversationRequest,
): Promise<ConversationWithMessages> {
  return request<ConversationWithMessages>(`/conversations/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export function archiveConversation(id: string): Promise<Conversation> {
  return request<Conversation>(`/conversations/${encodeURIComponent(id)}/archive`, {
    method: 'POST',
  });
}

export function unarchiveConversation(id: string): Promise<Conversation> {
  return request<Conversation>(`/conversations/${encodeURIComponent(id)}/unarchive`, {
    method: 'POST',
  });
}

export function deleteConversation(id: string): Promise<void> {
  return request<void>(`/conversations/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function getUploadUrl(storageObjectId: string): Promise<{ url: string }> {
  return request<{ url: string }>(`/uploads/${encodeURIComponent(storageObjectId)}/url`);
}

/**
 * Whether this org can transcribe audio server-side. Probed once per page
 * load by the composer's mic, and only on browsers without the Web Speech
 * API — Chrome and Safari recognize speech locally and never call this.
 */
export function getTranscriptionCapability(): Promise<TranscriptionCapability> {
  return request<TranscriptionCapability>('/chat/transcription');
}

export function transcribeAudio(body: TranscribeRequest): Promise<TranscribeResponse> {
  return request<TranscribeResponse>('/chat/transcription', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function sendControlResponse(agentId: string, payload: unknown): Promise<unknown> {
  return request(`/agents/${encodeURIComponent(agentId)}/control-response`, {
    method: 'POST',
    body: JSON.stringify({ payload }),
  });
}

/** Answer a capability card (a listener or action the agent asked to be allowed). */
export function decideCapabilityRequest(
  agentId: string,
  requestId: string,
  answer: AgentCapabilityAnswer,
  widen = false,
): Promise<unknown> {
  return request(
    `/agents/${encodeURIComponent(agentId)}/capability-requests/${encodeURIComponent(requestId)}/decision`,
    { method: 'POST', body: JSON.stringify({ answer, ...(widen ? { widen: true } : {}) }) },
  );
}
